import { useState } from 'react';
import toast from 'react-hot-toast';

import { recentFailedRequestId } from '@app/services/api/requestId';

import { Button } from '@components/index';

import Loader from '@components/Loader/ui/Loader';
import styles from './Toast.module.scss';

interface ToastProps {
  title: string;
  type: 'success' | 'error' | 'warning' | 'loading';
  text?: string;
  id?: string;
  className?: string;
  /** The failed request to name; defaults to the call that just failed. */
  reference?: string | null;
}

/**
 * @param props - The props for the component.
 * @description - Toast shared component
 * @returns A React component that displays a toast.
 */

function Toast(props: ToastProps) {
  const { title, text, type, id, className, reference } = props;
  // Read once, when the toast appears: a later failure must not relabel it.
  // "Ref: XXXX" is the id on every server log line for that call
  // (ZLAYT-1519).
  const [ref] = useState(() =>
    type === 'error' ? (reference ?? recentFailedRequestId()) : null
  );

  const iconElement =
    type === 'loading' ? (
      <Loader size={18} />
    ) : (
      <img src={`/${type}.svg`} alt={type} />
    );

  return (
    <div className={[styles.toast, className, styles[type]].join(' ')}>
      <div className={styles.img}>{iconElement}</div>
      <div className={styles.wrap}>
        <p className={styles.title}>{title}</p>
        <p className={styles.text}>{text}</p>
        {ref && <p className={styles.ref}>Ref: {ref}</p>}
      </div>
      {id && (
        <Button
          variant="underline"
          onClick={() => toast.dismiss(id)}
          className={styles.closeBtn}
        >
          <span />
        </Button>
      )}
    </div>
  );
}

export { Toast };
